<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ _('Create a new Scheduled task for...') }}</h4>
    </div>
    <div class="modal-body" v-if="!selectedType">
      <div class="form-group">
        <span v-if="taskTypes && taskTypes.length" class="template-separator types" style="cursor: initial">{{ _('Type') }}</span>
        <div class="template-wrap">
          <template v-for="taskType in taskTypes">
            <button type="button" class="btn btn-primary" v-on:click="selectType(taskType)"><i class="hidden fa fa-fw fa-5x fa-clock"></i><strong class="desc">{{ taskType.name }}</strong></button>{{ ' ' }}
          </template>
        </div>
      </div>
    </div>
    <div class="modal-body" v-else>
      <div class="row">
        <div class="col-xs-12">
          <h3>{{ selectedType.name }}</h3>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12">
          <label for="time">{{ _('Date') }}</label>
          <div class="form-group">
            <DatePicker :shortLocale="shortLocale" v-model="time" :time-picker="true" :time-picker-inline="true" />
          </div>
        </div>
      </div>
      <template v-if="selectedType.id === 'productAction'">
        <div class="row">
          <div class="col-xs-12">
            <div class="form-group">
              <label>{{ _('Action') }}</label>
              <select class="form-control" v-model="data.action">
                <option value="visibility">{{ _('Change Storefront visibility') }}</option>
                <option value="archive">{{ _('Archive product') }}</option>
                <option value="publish">{{ _('Restore from archive') }}</option>
              </select>
            </div>
            <template v-if="data.action === 'visibility'">
              <div class="form-group">
                <label>{{ _('Publishing mode') }}</label>
                <div class="btn-group" role="group" style="display: flex; flex-wrap: wrap; gap: 4px;">
                  <button type="button" :class="['btn btn-sm', localPublishingMode === 'none' ? 'btn-default active' : 'btn-default']" v-on:click="setLocalPublishingMode('none')">
                    <i class="fa fa-fw fa-eye-slash"></i> {{ _('Not published') }}
                  </button>
                  <button type="button" :class="['btn btn-sm', localPublishingMode === 'all' ? 'btn-success active' : 'btn-default']" v-on:click="setLocalPublishingMode('all')">
                    <i class="fa fa-fw fa-globe"></i> {{ _('Published to all') }}
                  </button>
                  <button type="button" :class="['btn btn-sm', localPublishingMode === 'selected' ? 'btn-primary active' : 'btn-default']" v-on:click="setLocalPublishingMode('selected')">
                    <i class="fa fa-fw fa-users"></i> {{ _('Published to selected people') }}
                  </button>
                </div>
              </div>
              <div class="form-group" v-if="localPublishingMode !== 'none'">
                <div class="well well-sm" style="margin-bottom: 8px">
                  <p style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 6px; color: #888">{{ _('Visible in') }}</p>
                  <div class="checkbox" style="margin-top: 0">
                    <label>
                      <input type="checkbox" class="globalCatalogCheckbox" :checked="enabledVersionsList.length > 0 && enabledVersionsList.length === storefrontversions.length" v-on:input="versionsChanged('global', $event.target.checked)" />
                      <strong>{{ _('All language versions') }}</strong>
                    </label>
                  </div>
                  <template v-if="storefrontversions && storefrontversions.length">
                    <hr style="margin: 6px 0">
                    <div class="checkbox" style="margin-top: 4px" v-for="version in storefrontversions" :key="version">
                      <label>
                        <input type="checkbox" class="versionCatalogCheckbox" :checked="enabledVersionsList.includes(version)" v-on:input="versionsChanged(version, $event.target.checked)" />
                        {{ getVersionName(version) }}
                      </label>
                    </div>
                  </template>
                </div>
                <p v-if="enabledVersionsList.length === 0" class="text-danger" style="margin-top: -4px; margin-bottom: 0"><i class="fa fa-fw fa-warning"></i> {{ _('At least one language must be selected.') }}</p>
              </div>
            </template>
            <div class="form-group">
              <label for="reportProducts">{{ _('Products') }}</label>
              <multiselect v-model="data.products" :options="allProducts" :multiple="true" :searchable="true" :allow-empty="true" :custom-label="nameWithId" label="name" track-by="id">
                <template #noResult>{{ _('No results found.') }}</template>
              </multiselect>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div class="modal-footer">
      <button class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
      <button class="btn btn-success" v-on:click="selected" :disabled="!canSubmit"><i class="fa fa-fw fa-save"></i> {{ _('OK') }}</button>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import constants from '~/lib/constants'
import { mapGetters } from 'vuex'
import Multiselect from 'vue-multiselect'
import DatetimePicker from '~/components/DatetimePicker'
import { DateTime } from 'luxon'

export default {
  mixins: [constants],
  props: ['preselectedType', 'preselectedAction', 'preselectedProduct', 'publishingMode', 'currentEnableCatalog', 'currentEnableCollection'],
  data () {
    const _ = useNuxtApp().$translate
    let taskTypes = [
      { 'id': 'productAction', 'name': _('Product') },
    ]
    return {
      taskTypes: taskTypes,
      selectedProducts: [],
      allProducts: [],
      selectedType: null,
      time: null,
      data: {},
      enable_catalog: this.currentEnableCatalog || '0',
      enable_collection: this.currentEnableCollection || '0',
      localPublishingMode: this.publishingMode || 'all'
    }
  },
  computed: {
    ...mapGetters(['allSettings', 'shopId', 'baseDomain', 'locale', 'apiKey', 'timezone']),
    settings () {
      return this.shopSettings(this.shopId)
    },
    storefrontversions () {
      if (!this.settings) {
        return []
      }
      return this.settings.storefrontversions ? this.settings.storefrontversions.split(';') : []
    },
    enabledVersionsList () {
      const raw = (this.enable_catalog && this.enable_catalog !== '0')
        ? this.enable_catalog
        : this.enable_collection
      if (!raw || raw === '0') return []
      if (raw === '1') return [...this.storefrontversions]
      return raw.split ? raw.split(';') : []
    },
    shortLocale () {
      return this.locale.split('_')[0]
    },
    visibilityModeIsValid () {
      return this.data.action !== 'visibility' ||
        this.localPublishingMode === 'none' ||
        this.enabledVersionsList.length > 0
    },
    canSubmit () {
      return this.selectedType && this.ts && this.visibilityModeIsValid
    },
    ts () {
      if (!this.time) {
        return null
      }
      return DateTime.fromJSDate(this.time, { zone: this.timezone }).toUnixInteger()
    },
    processedData () {
      const d = {}
      const type = this.selectedType ? this.selectedType.id : null
      d.action = this.data.action
      d.data = {}
      switch (type) {
        case 'productAction':
          d.data.targetIds = this.data.products.map(p => parseInt(p.id, 10))
          if (d.action === 'visibility') {
            d.data.enableCatalog = this.enable_catalog
            d.data.enableCollection = this.enable_collection
          }
          break
      }
      return d
    }
  },
  methods: {
    shopSettings (shopId) {
      return this.allSettings[shopId + '-' + this.locale] || {}
    },
    setLocalPublishingMode (newMode) {
      const currentVersions = this.enabledVersionsList.length
        ? this.enabledVersionsList
        : [...this.storefrontversions]
      const val = currentVersions.length ? currentVersions.join(';') : '0'
      this.localPublishingMode = newMode
      if (newMode === 'none') {
        this.enable_catalog = '0'
        this.enable_collection = '0'
      } else if (newMode === 'all') {
        this.enable_catalog = val
        this.enable_collection = val
      } else if (newMode === 'selected') {
        this.enable_catalog = '0'
        this.enable_collection = val
      }
    },
    versionsChanged (version, checked) {
      let versions
      if (version === 'global') {
        versions = checked ? [...this.storefrontversions] : []
      } else {
        versions = [...this.enabledVersionsList]
        if (checked) {
          if (!versions.includes(version)) versions.push(version)
        } else {
          versions = versions.filter(v => v !== version)
        }
      }
      const val = versions.length ? versions.join(';') : '0'
      if (this.localPublishingMode === 'all') {
        this.enable_catalog = val
        this.enable_collection = val
      } else if (this.localPublishingMode === 'selected') {
        this.enable_catalog = '0'
        this.enable_collection = val
      }
    },
    nameWithId ({ id, name }) {
      return id + ': ' + name
    },
    hideModal () {
      this.$emit('close')
    },
    selected () {
      this.$emit('selected', {
        type: this.selectedType.id,
        ts: this.ts,
        data: this.processedData
      })
    },
    selectType (type) {
      this.selectNone()
      this.selectedType = type
    },
    selectNone () {
      this.selectedType = null
    },
    async loadProducts () {
      let response = await axios.get(this.baseDomain + this.shopId + '/' + this.locale + '/products.json?version=' + this.locale + '&iSortCol_0=2&sSortDir_0=asc&iSortingCols=1&sSearch_10=both&simple=true')
      let ret = []
      if (response && response.data) {
        for (var i = 0; i < response.data.length; i++) {
          let d = response.data[i]
          ret.push({ id: d.id, name: d.name })
        }
      }
      this.allProducts = ret
    },
  },
  mounted () {
    if (this.preselectedType) {
      const t = this.taskTypes.find(tt => tt.id === this.preselectedType)
      if (t) {
        this.selectedType = t
      }
    }
    if (this.preselectedAction) {
      this.data.action = this.preselectedAction
    }
    if (this.preselectedProduct) {
      this.data.products = [this.preselectedProduct]
    }
  },
  watch: {
    selectedType: {
      deep: true,
      handler (newValue) {
        if (newValue && newValue.id === 'productAction' && !this.allProducts.length) {
          this.loadProducts()
        }
      }
    }
  },
  components: {
    DatetimePicker,
    Multiselect
  }
}
</script>
